import {
  Data,
  type LoginWithUserCredentialsRequest,
  loginService,
  setConnectAccessToken,
} from '@/connect';
import router from '@/router/router';
import { store as vuex } from '@/store/store';
import type { JWT } from '@/utils/helpers/socket';
import { disposeAllStores } from '@/utils/helpers/pinia';
import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, watch } from 'vue';
import { useSocketStore } from './socket-store';
import { useStoredTabSyncRef } from '@/composables/stored-tab-sync-ref';

const CHANNEL_MULTITAB_JWT = 'CHANNEL_MULTITAB_JWT';

// time between heartbeats in ms: m * s * ms
export const HEARTBEAT_INTERVAL = 5 * 60 * 1000;

export const useAuthStore = defineStore('auth', () => {
  const socketStore = useSocketStore();

  let loginEmail: string | null = null;

  const jwt = useStoredTabSyncRef<JWT | null>('JWT', null);

  watch(
    () => jwt.value,
    async (valueNew, valueOld) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const wasLoggedIn = !!valueOld;
      const isLoggedIn = !!valueNew;

      if (wasLoggedIn && !isLoggedIn) {
        if (router.currentRoute.name !== 'logout') {
          // if we were previously logged-in and another tab sends a not-logged-in token then we should logout as well
          await router.replace({ name: 'logout' });
        }
      } else if (!wasLoggedIn && isLoggedIn) {
        // if we were previously not logged-in two cases will send us here:
        // 1. sucessful login form submission
        // 2. another tab sends a logged-in token
        // then we should authenticate (happens when assigning jwt.value) and go to the homepage
        await router.replace({ name: 'home' });
      }
    }
  );

  // when the socket opens we need to authenticate to it
  socketStore.onOpen(authentifyWebSocket);
  // when the socket receives a JWT token from the backend we need to store it
  // (for when we're waiting identity verification step)
  socketStore.useEventHandler('sendJWT', (tokens: JWT) => (jwt.value = tokens));

  let heartbeatIntervalId: number | null = null;

  const heartbeat = {
    start() {
      if (heartbeatIntervalId !== null) return; // already started
      heartbeatIntervalId = window.setInterval(
        () =>
          socketStore.send({
            action: 'sessionHeartbeat',
            payload: HEARTBEAT_INTERVAL.toString(),
          }),
        HEARTBEAT_INTERVAL
      );
    },
    stop() {
      if (heartbeatIntervalId === null) return; // already stopped
      window.clearInterval(heartbeatIntervalId);
      heartbeatIntervalId = null;
    },
  };

  async function authentifyWebSocket() {
    const accessToken = await refreshAuthTokensIfNecessary();
    if (accessToken === null) return;
    await socketStore.send({ action: 'authenticate', payload: accessToken }, ['is-authenticated']);
    socketStore.lockSocket.unlock('is-authenticated');
    heartbeat.start();
  }

  let timeoutId: null | number = null;
  watch(
    jwt,
    async (tokens, tokensOld) => {
      const { accessToken = null, refreshToken = null } = tokens ?? {};

      // For connect-es
      setConnectAccessToken(accessToken);

      if (timeoutId !== null) clearTimeout(timeoutId);

      const axiosCommonHeaders = (axios.defaults.headers.common ??= {});

      // just logged out
      if (accessToken === null || refreshToken === null) {
        const isFirstWatchPass = tokensOld === undefined;
        if (isFirstWatchPass) return;

        heartbeat.stop();

        // clear all sessionStorage to avoid inconsistent state
        // (i.e. snapshot-manager, potentially others in the future)
        sessionStorage.clear();

        // useStoredTabSyncRef clones from sessionStorage to localStorage
        // which can lead to inconsistent state when we are developing locally
        // (logging in with different users, coding from different branches, etc.)
        // if needed, set "VITE_SUPPRESS_LOCAL_STORAGE_CLEAR=true" in .env.local
        // to override the default behavior
        if (
          !vuex.state.clientConfig?.systemProdLike &&
          !process.env.VITE_SUPPRESS_LOCAL_STORAGE_CLEAR
        ) {
          localStorage.clear();
        }

        // reset axios Authorization header
        delete axiosCommonHeaders.Authorization;

        // dispose all Pinia stores to prevent data leakage between users
        disposeAllStores();

        // handle vuex non-migrated stuff
        await vuex.dispatch('logout');

        socketStore.reconnect();
        return;
      }

      // just logged in or just loaded the page where is already logged in
      if (tokens && !tokensOld && socketStore.isConnected) {
        void authentifyWebSocket();
      }

      axiosCommonHeaders.Authorization = `Bearer ${accessToken}`;

      timeoutId = window.setTimeout(
        () => tryRefreshAuthTokens(refreshToken),
        refreshAccessTokenIn(accessToken)
      );
    },
    { immediate: true, flush: 'sync' }
  );

  async function tryRefreshAuthTokens(refreshToken: string) {
    await navigator.locks.request(CHANNEL_MULTITAB_JWT, { ifAvailable: true }, async (lock) => {
      if (lock === null) return; // only refresh token if we obtained lock
      const result = await loginService.refreshToken({ token: refreshToken });
      jwt.value = result.success ? result.data : null;
      if (!result.success) {
        await logout();
        return router.replace({ name: 'login', params: { error: result.error } });
      }
    });
  }

  async function refreshAuthTokensIfNecessary() {
    // can't refresh tokens if we don't have state
    if (jwt.value === null) return null;

    const { accessToken = null } = jwt.value ?? {};

    // only refresh if we don't have a token, or if the token we have needs to be refreshed
    if (accessToken === null || refreshAccessTokenIn(accessToken) < 0) {
      return await refreshAuthTokens();
    }

    return accessToken;
  }

  async function refreshAuthTokens() {
    // can't refresh tokens if we don't have state
    if (jwt.value === null) return null;
    // attempt to refresh using our refresh token
    await tryRefreshAuthTokens(jwt.value.refreshToken);
    return jwt.value?.accessToken ?? null;
  }

  async function onLogin2FARequired(email: string, loginPassword: string) {
    await router.push({ name: 'login-2fa', params: { loginEmail: email, loginPassword } });
  }

  async function onLoginConfirmIdentityRequired(email: string) {
    await socketStore.send(
      {
        action: 'socketConfirmIdentityWait',
        payload: email,
      },
      ['is-authenticated']
    );
    await router.push({ name: 'confirm-identity', params: { loginEmail: email } });
  }

  async function onIdentityConfirmedButUserNotLoggedIn(email: string) {
    // If the password is missing or from another user we cannot decrypt the auctions
    // => ask the user to login again so that we can decrypt
    await router.replace({
      name: 'login',
      params: { email, message: 'loginIdentityConfirmationSucceeded' },
    });
  }

  async function login(credentials: Data<LoginWithUserCredentialsRequest>) {
    loginEmail = credentials.email;
    const result = await loginService.loginWithUserCredentials(credentials);
    if (result.success) {
      jwt.value = result.data;
    } else {
      switch (result.error) {
        case 'rest.LoginIncorrect':
        case 'rest.LoginTooManyAttempts':
          break;
        case 'rest.Login2FARequired':
          await onLogin2FARequired(credentials.email, credentials.password);
          return;
        case 'rest.LoginConfirmIdentityRequired':
          await onLoginConfirmIdentityRequired(credentials.email);
          return;
      }
    }
    return result;
  }

  async function loginWithSAMLTicket(ticket: string) {
    const result = await loginService.loginWithSAMLTicket(ticket);
    if (result.success) {
      jwt.value = result.data;
    }
    return result;
  }

  async function logout() {
    try {
      if (jwt.value !== null) {
        // this may fail if token expired
        await loginService.logout();
      }
    } finally {
      loginEmail = null;
      jwt.value = null;
    }
  }

  async function confirmIdentity(email: string, secret: string) {
    const result = await loginService.confirmIdentity({ secret });
    if (result.success) {
      if (loginEmail !== email) {
        await onIdentityConfirmedButUserNotLoggedIn(email);
      } else {
        await router.replace({ name: 'home' });
      }
    }
    return result;
  }

  const user = computed(() => vuex.state.loginState.user);

  return {
    login,
    loginWithSAMLTicket,
    logout,
    confirmIdentity,
    isUserAuthenticated: computed(() => !!jwt.value),
    refreshAuthTokens,
    user,
  };
});

export type AuthStore = ReturnType<typeof useAuthStore>;

function refreshAccessTokenIn(accessToken: string): number {
  const jwtPayload = JSON.parse(atob(accessToken.split('.')[1]));
  const expirationTime = jwtPayload.exp * 1000;
  const expiresIn = expirationTime - Date.now();
  return expiresIn * 0.9;
}
